import { defineStore } from "pinia";

/**
 * Sprint F — dynamische Facet-Filter pro Kategorie.
 *
 * Quelle: `GET /api/categories/:slug/filters` (Sprint E `CategoryFilterService`).
 * Liefert pro Kategorie welche `Product.attributes`-Keys als Filter sinnvoll sind
 * (Coverage ≥ 20 %) und welche Top-Werte es pro Key gibt.
 */
export interface CategoryFilterDefinition {
    enabled: string[];
    values: Record<string, string[]>;
    sampleSize: number;
    generatedAt: string;
}

const EMPTY: CategoryFilterDefinition = {
    enabled: [],
    values: {},
    sampleSize: 0,
    generatedAt: new Date(0).toISOString(),
};

export const useCategoryFiltersStore = defineStore("categoryFilters", () => {
    const definition = ref<CategoryFilterDefinition>({ ...EMPTY });
    const selected = ref<Record<string, string[]>>({});
    const loadedSlug = ref<string | null>(null);
    const loading = ref(false);

    const hasFilters = computed(() => definition.value.enabled.length > 0);

    const activeCount = computed(() =>
        Object.values(selected.value).reduce((n, vals) => n + vals.length, 0),
    );

    /** API-Response → Store. Keine Fehler werfen — leerer State ist OK. */
    async function load(slug: string) {
        if (!slug) return;
        if (loadedSlug.value === slug) return;
        const api = useApi();
        loading.value = true;
        try {
            const data = await api<CategoryFilterDefinition>(
                `/api/categories/${encodeURIComponent(slug)}/filters`,
            );
            definition.value = data ?? { ...EMPTY };
            loadedSlug.value = slug;
        } catch {
            definition.value = { ...EMPTY };
            loadedSlug.value = slug;
        } finally {
            loading.value = false;
        }
    }

    function toggle(key: string, value: string) {
        const current = selected.value[key] ?? [];
        const next = current.includes(value)
            ? current.filter((v) => v !== value)
            : [...current, value];
        if (next.length === 0) {
            const { [key]: _removed, ...rest } = selected.value;
            selected.value = rest;
        } else {
            selected.value = { ...selected.value, [key]: next };
        }
    }

    function setAll(next: Record<string, string[]>) {
        const cleaned: Record<string, string[]> = {};
        for (const [k, v] of Object.entries(next)) {
            if (Array.isArray(v) && v.length > 0) cleaned[k] = [...v];
        }
        selected.value = cleaned;
    }

    function reset() {
        selected.value = {};
    }

    return {
        definition,
        selected,
        loadedSlug,
        loading,
        hasFilters,
        activeCount,
        load,
        toggle,
        setAll,
        reset,
    };
});
